import React, { useEffect, useState, useCallback } from "react";
import { api, fmtINR } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Wallet, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Stat = ({ label, value, accent, testId, Icon }) => (
  <div data-testid={testId} className="bg-[#121212] border border-white/10 p-8 relative group">
    <div className="flex items-start justify-between mb-8">
      <span className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</span>
      <Icon className="w-4 h-4 text-neutral-600 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.5} />
    </div>
    <div className={`font-mont text-3xl ${accent || "text-white"}`}>{value}</div>
  </div>
);

export default function Dashboard() {
  const { profiles, activeProfileId, setActiveProfileId } = useProfiles();
  const profileId = activeProfileId || profiles[0]?.id;
  const [stats, setStats] = useState({ total_invoiced: 0, total_paid: 0, total_due: 0, invoice_count: 0 });
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);

  const load = useCallback(async () => {
    if (!profileId) { setStats({ total_invoiced: 0, total_paid: 0, total_due: 0, invoice_count: 0 }); setInvoices([]); setClients([]); return; }
    const [s, inv, cl] = await Promise.all([
      api.get(`/stats?profile_id=${profileId}`),
      api.get(`/invoices?profile_id=${profileId}`),
      api.get(`/clients?profile_id=${profileId}`),
    ]);
    setStats(s.data);
    setInvoices(inv.data);
    setClients(cl.data);
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const recent = invoices.slice(0, 6);

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8 lg:mb-12">
        <div>
          {profiles.length > 1 && (
            <Select value={profileId} onValueChange={setActiveProfileId}>
              <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-full sm:w-56 mb-3 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                {profiles.map((p) => (<SelectItem key={p.id} value={p.id} className="font-mont text-sm">{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">— Overview</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">Dashboard</h1>
        </div>
        <span className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">
          {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-8 lg:mb-12">
        <Stat testId="stat-invoiced" label="Total Invoiced" value={fmtINR(stats.total_invoiced)} Icon={FileText} />
        <Stat testId="stat-paid" label="Total Received" value={fmtINR(stats.total_paid)} accent="text-emerald-400" Icon={Wallet} />
        <Stat testId="stat-due" label="Balance Due" value={fmtINR(stats.total_due)} accent="text-[#D4AF37]" Icon={Clock} />
        <Stat testId="stat-count" label="Invoice Count" value={String(stats.invoice_count).padStart(3, "0")} Icon={ArrowUpRight} />
      </div>

      <section>
        <div className="flex items-baseline justify-between mb-6 border-b border-white/10 pb-3">
          <h2 className="font-serif text-xl sm:text-2xl text-white">Recent Invoices</h2>
          <Link
            to="/invoices"
            data-testid="view-all-invoices"
            className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-[#D4AF37]"
          >
            View all →
          </Link>
        </div>

        {recent.length === 0 ? (
          <div className="py-20 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">
            No invoices yet — create your first.
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {recent.map((inv) => (
              <Link
                key={inv.id}
                to={`/invoices/${inv.id}`}
                data-testid={`recent-invoice-${inv.invoice_number}`}
                className="block md:grid md:grid-cols-12 gap-4 py-4 md:py-5 md:items-center hover:bg-white/[0.02] px-2 transition-colors"
              >
                <div className="flex md:hidden items-center justify-between mb-1">
                  <span className="font-mont text-sm text-[#D4AF37]">{inv.invoice_number}</span>
                  <span className="font-mont text-xs text-neutral-500">{inv.issue_date}</span>
                </div>
                <div className="flex md:hidden items-center justify-between">
                  <span className="text-sm text-white truncate">{clientName(inv.client_id)}</span>
                  <span className="font-mont text-sm text-white">{fmtINR(inv.totals?.grand_total)}</span>
                </div>

                <div className="hidden md:block col-span-2 font-mont text-sm text-[#D4AF37]">{inv.invoice_number}</div>
                <div className="hidden md:block col-span-4 text-sm text-white truncate">{clientName(inv.client_id)}</div>
                <div className="hidden md:block col-span-2 font-mont text-xs text-neutral-500">{inv.issue_date}</div>
                <div className="hidden md:block col-span-2 font-mont text-sm text-white text-right">{fmtINR(inv.totals?.grand_total)}</div>
                <div className="hidden md:block col-span-2 font-mont text-xs text-right">
                  <span className={inv.totals?.balance_due <= 0 ? "text-emerald-400" : "text-[#D4AF37]"}>
                    {fmtINR(inv.totals?.balance_due)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
